import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { getTodayAEST, formatDateForInput } from '../../utils/dateUtils';
import { uploadFile } from '../../utils/fileUpload';
import { X, Package, Calendar, FileText, Building2, Camera, Loader2 } from 'lucide-react';

interface WasteTracking {
  id: string;
  waste_quantity_id: string;
  amount: number;
  date: string;
  notes?: string;
  receipt_photo_url?: string;
  created_at: string;
  updated_at: string;
}

interface WasteQuantity {
  id: string;
  site_id: string;
  waste_type: string;
  amount: number;
  unit: string;
  site?: {
    id: string;
    name: string;
    address: string;
  };
}

interface WasteTrackingFormProps {
  initialData?: WasteTracking | null;
  onSubmit: (trackingData: Partial<WasteTracking>) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
}

export const WasteTrackingForm: React.FC<WasteTrackingFormProps> = ({ 
  initialData, 
  onSubmit, 
  onCancel, 
  submitting = false 
}) => {
  const [sites, setSites] = useState<{ id: string; name: string; address: string }[]>([]);
  const [wasteQuantities, setWasteQuantities] = useState<WasteQuantity[]>([]);
  const [loadingQuantities, setLoadingQuantities] = useState(true);
  const [loadingSites, setLoadingSites] = useState(true);
  const [selectedQuantity, setSelectedQuantity] = useState<WasteQuantity | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [availableWasteTypes, setAvailableWasteTypes] = useState<WasteQuantity[]>([]);

  const [formData, setFormData] = useState({
    waste_quantity_id: initialData?.waste_quantity_id || '',
    amount: initialData?.amount || 0,
    date: initialData?.date ? formatDateForInput(initialData.date) : getTodayAEST(),
    notes: initialData?.notes || '',
    receipt_photo_url: initialData?.receipt_photo_url || ''
  });

  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState<string>(initialData?.receipt_photo_url || '');

  useEffect(() => {
    fetchSites();
    fetchWasteQuantities();
    
    // If editing existing data, set up the initial state
    if (initialData) {
      // Find the waste quantity to get the site ID
      fetchWasteQuantities().then(() => {
        const quantity = wasteQuantities.find(q => q.id === initialData.waste_quantity_id);
        if (quantity?.site_id) {
          setSelectedSiteId(quantity.site_id);
        }
      });
    }
  }, [initialData]);

  useEffect(() => {
    // When editing an existing tracking entry or when waste quantities load, pre-select the site
    if (initialData && wasteQuantities.length > 0) {
      const existingQuantity = wasteQuantities.find(q => q.id === initialData.waste_quantity_id);
      if (existingQuantity?.site_id) {
        setSelectedSiteId(existingQuantity.site_id);
        setSelectedQuantity(existingQuantity);
      }
    }
  }, [initialData, wasteQuantities]);

  useEffect(() => {
    // Filter waste types based on selected site
    if (selectedSiteId) {
      const filteredTypes = wasteQuantities.filter(q => q.site_id === selectedSiteId);
      setAvailableWasteTypes(filteredTypes);
      
      // Clear waste type selection if current selection is not available for the new site
      if (formData.waste_quantity_id && !initialData) {
        const currentQuantity = wasteQuantities.find(q => q.id === formData.waste_quantity_id);
        if (currentQuantity?.site_id !== selectedSiteId) {
          setFormData(prev => ({ ...prev, waste_quantity_id: '' }));
          setSelectedQuantity(null);
        }
      }
    } else {
      setAvailableWasteTypes([]);
    }
  }, [selectedSiteId, wasteQuantities, formData.waste_quantity_id, initialData]);
  
  useEffect(() => {
    if (formData.waste_quantity_id) {
      const quantity = wasteQuantities.find(q => q.id === formData.waste_quantity_id);
      setSelectedQuantity(quantity || null);
    } else {
      setSelectedQuantity(null);
    }
  }, [formData.waste_quantity_id, wasteQuantities]);

  const fetchSites = async () => {
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('id, name, address')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setSites(data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoadingSites(false);
    }
  };
  
  const fetchWasteQuantities = async () => {
    try {
      const { data, error } = await supabase
        .from('waste_quantities')
        .select(`
          *,
          sites!inner(id, name, address, status)
        `)
        .eq('sites.status', 'active')
        .order('name', { foreignTable: 'sites' });

      if (error) throw error;

      const quantitiesWithSites: WasteQuantity[] = (data || []).map(item => ({
        ...item,
        site: item.sites
      }));

      setWasteQuantities(quantitiesWithSites);
    } catch (error) {
      console.error('Error fetching waste quantities:', error);
    } finally {
      setLoadingQuantities(false);
    }
  };

  const handleReceiptUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploadingReceipt(true);
    try {
      const fileName = `waste-receipt-${Date.now()}-${file.name}`;
      const url = await uploadFile(file, 'waste-receipts', fileName);

      setFormData(prev => ({
        ...prev,
        receipt_photo_url: url
      }));
      setReceiptPreview(url);
    } catch (error) {
      console.error('Error uploading receipt:', error);
      alert('Failed to upload receipt. Please try again.');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleRemoveReceipt = () => {
    setFormData(prev => ({
      ...prev,
      receipt_photo_url: ''
    }));
    setReceiptPreview('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trackingData = {
      ...formData,
      amount: Number(formData.amount)
    };

    await onSubmit(trackingData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const siteId = e.target.value;
    setSelectedSiteId(siteId);
    // Clear waste type selection when site changes (but not when editing existing data)
    if (!initialData) {
      setFormData(prev => ({ ...prev, waste_quantity_id: '' }));
      setSelectedQuantity(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Package className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              {initialData ? 'Edit Waste Tracking' : 'Add Waste Tracking Entry'}
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Site Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="inline h-4 w-4 mr-1" />
              Select Site *
            </label>
            {loadingSites ? (
              <div className="flex items-center justify-center p-4 border border-gray-300 rounded-md">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
                <span className="ml-2 text-sm text-gray-600">Loading sites...</span>
              </div>
            ) : (
              <select
                value={selectedSiteId}
                onChange={handleSiteChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Choose a site</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.name} - {site.address}
                  </option>
                ))}
              </select>
            )}
            {sites.length === 0 && !loadingSites && (
              <p className="text-sm text-gray-500 mt-1">
                No active sites found. Please create a site first.
              </p>
            )}
          </div>

          {/* Waste Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Package className="inline h-4 w-4 mr-1" />
              Select Waste Type *
            </label>
            {!selectedSiteId ? (
              <div className="p-4 border border-gray-300 rounded-md bg-gray-50">
                <p className="text-sm text-gray-500 text-center">Please select a site first</p>
              </div>
            ) : loadingQuantities ? (
              <div className="flex items-center justify-center p-4 border border-gray-300 rounded-md">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
                <span className="ml-2 text-sm text-gray-600">Loading waste types...</span>
              </div>
            ) : (
              <select
                name="waste_quantity_id"
                required
                value={formData.waste_quantity_id}
                onChange={handleChange}
                disabled={!selectedSiteId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Choose waste type</option>
                {availableWasteTypes.map(quantity => (
                  <option key={quantity.id} value={quantity.id}>
                    {quantity.waste_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                    ({quantity.amount} {quantity.unit} estimated)
                  </option>
                ))}
              </select>
            )}
            {selectedSiteId && availableWasteTypes.length === 0 && !loadingQuantities && (
              <p className="text-sm text-gray-500 mt-1">
                No waste estimates found for this site. Please set up waste estimates first.
              </p>
            )}
          </div>

          {/* Selected Site & Waste Type Summary */}
          {selectedSiteId && selectedQuantity && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Selection Summary</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p><strong>Site:</strong> {sites.find(s => s.id === selectedSiteId)?.name}</p>
                <p><strong>Address:</strong> {sites.find(s => s.id === selectedSiteId)?.address}</p>
                <p><strong>Waste Type:</strong> {selectedQuantity.waste_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                <p><strong>Estimated Amount:</strong> {selectedQuantity.amount} {selectedQuantity.unit}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Package className="inline h-4 w-4 mr-1" />
                Amount Removed *
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="amount"
                  required
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="0.00"
                />
                {selectedQuantity && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-sm">{selectedQuantity.unit}</span>
                  </div>
                )}
              </div>
              {selectedQuantity && (
                <p className="text-xs text-gray-500 mt-1">
                  Remaining estimate: {Math.max(0, selectedQuantity.amount - (formData.amount || 0)).toFixed(2)} {selectedQuantity.unit}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                Date *
              </label>
              <input
                type="date"
                name="date"
                required
                value={formData.date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Notes
            </label>
            <textarea
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Optional notes about the waste removal (e.g., truck details, disposal location, etc.)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Example: "Truck #5 - Load to Recycling Center", "Emergency pickup - Asbestos to licensed facility"
            </p>
          </div>

          {/* Receipt Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Camera className="inline h-4 w-4 mr-1" />
              Receipt Photo
            </label>

            {receiptPreview ? (
              <div className="space-y-3">
                <div className="relative inline-block">
                  <img
                    src={receiptPreview}
                    alt="Receipt preview"
                    className="max-w-full h-48 object-contain border border-gray-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveReceipt}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    title="Remove receipt"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <label className="flex-1 cursor-pointer">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-400 transition-colors">
                    {uploadingReceipt ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-8 w-8 text-orange-500 animate-spin mb-2" />
                        <p className="text-sm text-gray-600">Uploading receipt...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Camera className="h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600 mb-1">Click to upload receipt photo</p>
                        <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleReceiptUpload}
                    disabled={uploadingReceipt}
                    className="hidden"
                  />
                </label>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Upload a photo of the waste removal receipt for record keeping
            </p>
          </div>

          {/* Quick action buttons for common amounts */}
          {selectedQuantity && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Quick Amount Selection:</p>
              <div className="flex flex-wrap gap-2">
                {[0.5, 1, 2, 5, 10].map(amount => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, amount }))}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors"
                  >
                    {amount} {selectedQuantity.unit}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, amount: selectedQuantity.amount }))}
                  className="px-3 py-1 bg-orange-100 border border-orange-300 rounded-md text-sm hover:bg-orange-200 transition-colors text-orange-800"
                >
                  Full Amount ({selectedQuantity.amount} {selectedQuantity.unit})
                </button>
              </div>
            </div>
          )}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedSiteId || !formData.waste_quantity_id}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Saving...' : (initialData ? 'Update Entry' : 'Add Entry')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};